import { useContext, useEffect, useMemo, useState } from "react";
import SecondaryButton from "../../ui/SecondaryButton";
import ApplicationContext from "../../ApplicationContext";
import useTableStorage from "../../hooks/useTableStorage";
import useGlobalOkDialog from "../../hooks/useGlobalOkDialog";
import { ApplicableTableSettings } from "../../tableSettings";
import { volumeByCargoTypeYearsTable } from "./tableSettings";
import SmartTable from "../../uiControls/SmartTable";
import numeral from "numeral";
import { JobNames } from "../../framework.core/JobNames";

export interface VolumebyCargoTypeYearsModel {
  // CBM Model
  CargoType: string,

  FreeCbm5: number,
  FreeCbm4: number,
  FreeCbm3: number,
  FreeCbm2: number,
  FreeCbm1: number,

  NomiCbm5: number,
  NomiCbm4: number,
  NomiCbm3: number,
  NomiCbm2: number,
  NomiCbm1: number,

  ColoadCbm5: number,
  ColoadCbm4: number,
  ColoadCbm3: number,
  ColoadCbm2: number,
  ColoadCbm1: number,

  // TEU Model
  Teu20_5: number,
  Teu20_4: number,
  Teu20_3: number,
  Teu20_2: number,
  Teu20_1: number,

  Teu40_5: number,
  Teu40_4: number,
  Teu40_3: number,
  Teu40_2: number,
  Teu40_1: number,

  Teu45_5: number,
  Teu45_4: number,
  Teu45_3: number,
  Teu45_2: number,
  Teu45_1: number,

  TeuHQ_5: number,
  TeuHQ_4: number,
  TeuHQ_3: number,
  TeuHQ_2: number,
  TeuHQ_1: number,
}

export interface IVolumeCargoTypeYearsTable {
  group: string,
  type: string,
  amount5: number,
  amount4: number,
  amount3: number,
  amount2: number,
  amount1: number,
}

function TypeRenderer({ cell, data, row }: any) {
  const l = data[row.index];
  
  return (
    <div className="ifms-table-align-left">
      {l.type}
    </div>
  );
}

function AmountRenderer(label: string) {
  return ({ data, row }: any) => {
    const l = data[row.index];

    let amount = '';
    const cellValue = l[label];

    if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
      const format = `0,0.${'0'.repeat(2)}`;
      amount = numeral(cellValue).format(format);
    }
    return (
      <div className="ifms-table-align-right">{parseFloat(amount) === 0 ? null : amount}</div>
    );
    
  };
}

function VolumebyCargoTypeYearsImpl({
  onPopupClose,
  jobName,
  toDate,
  unit,
} : {
  onPopupClose: () => void,
  jobName: string,
  toDate: string,
  unit: string,
}) {
  const appContext = useContext(ApplicationContext);
  const tableStorage = useTableStorage();
  const [initialized, setInitialized] = useState<boolean>(false);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [volumeCargoTypeData] = useState<IVolumeCargoTypeYearsTable[]>([]);
  const year = new Date(toDate).getFullYear();

  async function loadData() {
    const apiResult = await appContext.api.getVolumebyCargoTypeYears(jobName, unit, toDate);
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(<p>Fail load data.</p>, false);
    } else {
      (apiResult?.result as VolumebyCargoTypeYearsModel[]).forEach(x => {
        createTable(x);
      });

      setInitialized(true);
    }
  }

  function createTable(data: VolumebyCargoTypeYearsModel) {
    if (unit === "TEU") {
      volumeCargoTypeData.push({ group: "line", type: "\u00A0 \u00A0 \u00A0 \u00A0 \u00A0 \u00A0 \u00A020", amount5: data.Teu20_5, amount4: data.Teu20_4, amount3: data.Teu20_3, amount2: data.Teu20_2, amount1: data.Teu20_1 });
      volumeCargoTypeData.push({ group: "line", type: "\u00A0 \u00A0 \u00A0 \u00A0 \u00A0 \u00A0 \u00A040", amount5: data.Teu40_5, amount4: data.Teu40_4, amount3: data.Teu40_3, amount2: data.Teu40_2, amount1: data.Teu40_1 });
      volumeCargoTypeData.push({ group: "line", type: "\u00A0 \u00A0 \u00A0 \u00A0 \u00A0 \u00A0 \u00A045", amount5: data.Teu45_5, amount4: data.Teu45_4, amount3: data.Teu45_3, amount2: data.Teu45_2, amount1: data.Teu45_1 });
      volumeCargoTypeData.push({ group: "line", type: "\u00A0 \u00A0 \u00A0 \u00A0 \u00A0 \u00A0 \u00A0HQ", amount5: data.TeuHQ_5, amount4: data.TeuHQ_4, amount3: data.TeuHQ_3, amount2: data.TeuHQ_2, amount1: data.TeuHQ_1 });
    } else {
      let type = "";

      switch (data.CargoType) {
        case "F": type = "FCL"; break;
        case "L": type = "LCL"; break;
        case "C": type = "Consol/FAK";  break;
      }
  
      if (type) volumeCargoTypeData.push({ group: "gh", type: type, amount5: 0, amount4: 0, amount3: 0, amount2: 0, amount1: 0 });
      
      volumeCargoTypeData.push({ group: "line", type: "\u00A0 \u00A0 \u00A0 \u00A0 \u00A0Free",    amount5: data.FreeCbm5,   amount4: data.FreeCbm4,   amount3: data.FreeCbm3,   amount2: data.FreeCbm2,   amount1: data.FreeCbm1,   });
      volumeCargoTypeData.push({ group: "line", type: "\u00A0 \u00A0 \u00A0 \u00A0 \u00A0Nomi",    amount5: data.NomiCbm5,   amount4: data.NomiCbm4,   amount3: data.NomiCbm3,   amount2: data.NomiCbm2,   amount1: data.NomiCbm1,   });
      volumeCargoTypeData.push({ group: "line", type: "\u00A0 \u00A0 \u00A0 \u00A0 \u00A0Co-Load", amount5: data.ColoadCbm5, amount4: data.ColoadCbm4, amount3: data.ColoadCbm3, amount2: data.ColoadCbm2, amount1: data.ColoadCbm1, });
    }
  }

  const labels = useMemo(() => {
    switch (jobName) {
      case JobNames.OIMbl:
      case JobNames.OEMbl:
        return {
          typeColumn: unit,
          gridHeight: unit === "TEU" ? '102px' : '262px'
        }
      case JobNames.AIMbl:
      case JobNames.AEMbl:
        return {
          typeColumn: unit === "GW" ? "Gross Weight" : "Chargeable Weight",
          gridHeight: '82px'
        }
      default:
        return {
          typeColumn: "",
          gridHeight: '262px'
        }
    }
  }, [jobName, unit]);

  useEffect(() => {
    if (!initialized) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobName, unit]);

  if (!initialized) {
    return (
      <div style={{ minHeight: '400px' }}></div>
    );
  }

  const tableOptions: ApplicableTableSettings = {
    tableName: `volumebyCargoTypeYears-${jobName}-${unit}`,
    ...volumeByCargoTypeYearsTable,
    height: labels.gridHeight,
    data: volumeCargoTypeData,
    rowRenderers: [
      { accessor: 'type', Cell: TypeRenderer, Header: () => <span>{labels.typeColumn}</span> },
      { accessor: 'amount5', Cell: AmountRenderer('amount5'), Header: () => <span>{year}</span> },
      { accessor: 'amount4', Cell: AmountRenderer('amount4'), Header: () => <span>{year-1}</span> },
      { accessor: 'amount3', Cell: AmountRenderer('amount3'), Header: () => <span>{year-2}</span> },
      { accessor: 'amount2', Cell: AmountRenderer('amount2'), Header: () => <span>{year-3}</span> },
      { accessor: 'amount1', Cell: AmountRenderer('amount1'), Header: () => <span>{year-4}</span> },
    ],
    customStorage: tableStorage,
    // getCustomCellProps: (currentProps: any, cell: any, rowIndex: any) => {
    //   if (cell.row.original.group === "gh") {
    //     currentProps.style.fontWeight = 'bold';
    //   }
    //   return currentProps;
    // }
  };

  return (
    <div className='report-popup'>
      <div style={{ width: '650px' }}>
        <SmartTable {...tableOptions} />
      </div>

      <div className='report-footer'>
        <SecondaryButton
          size="regular"
          style={{ width: '90px' }}
          onClick={onPopupClose}>
          CLOSE
        </SecondaryButton>
      </div>
    </div>
  )
}

const VolumebyCargoTypeYears = ({
  onPopupClose,
  jobName,
  toDate,
  unit
} : {
  onPopupClose: () => void,
  jobName: string,
  toDate: string,
  unit: string,
}) => {
  return (
    <VolumebyCargoTypeYearsImpl 
      key={`volume-cargotype-years-details`}
      onPopupClose={onPopupClose}
      jobName={jobName}
      toDate={toDate}
      unit={unit}
    />
  );
}

export default VolumebyCargoTypeYears;